/**
 * `StarRating` — scala a 10 stelle intere corrispondente a valori interi `0..10`.
 *
 * Design ref: "Componenti chiave" → `AssessmentWizard` / `StarRating` a 10
 * stelle intere = valori `0..10` (6.3, 7.3). *Validates: 6.3, 6.4, 7.3.*
 *
 * Il componente distingue **tre stati** del voto (allineati al modello dati e
 * al DTO `AssessmentView`, dove i voti assenti sono `null` e distinti da `0`):
 *  - `null`  → **assente** / non ancora votato (skip): nessuna stella piena e
 *    lo stato è visualmente marcato come "non votato";
 *  - `0`     → **voto valido pari a 0**: nessuna stella selezionata, ma è un
 *    voto vero e proprio, distinto dall'assenza (`null`);
 *  - `1..10` → **voto**: il numero corrispondente di stelle piene.
 *
 * L'interazione produce solo valori interi `0..10`:
 *  - cliccare la stella `n` (1..10) imposta il voto `n`;
 *  - il componente offre un controllo esplicito per impostare il voto `0`
 *    (nessuna stella selezionata), così da poter registrare `0` come voto
 *    distinto dall'assenza di voto.
 */
import { useState } from 'react';

/** Numero fisso di stelle: scala intera `0..10` (10 stelle). */
export const MAX_STARS = 10;

/**
 * Valore del voto:
 *  - `null`  → assente (non votato / skip);
 *  - `0..10` → voto (con `0` = voto zero, nessuna stella piena).
 */
export type StarRatingValue = number | null;

export interface StarRatingProps {
  /** Valore corrente del voto (`null` = assente, `0..10` = voto). */
  value: StarRatingValue;
  /**
   * Notifica un nuovo voto scelto dall'utente. Emette sempre un intero `0..10`
   * (mai `null`): la selezione è un voto, l'assenza deriva dallo skip a monte.
   */
  onChange?: (value: number) => void;
  /** Disabilita l'interazione (sola lettura). */
  readOnly?: boolean;
  /** Etichetta accessibile del gruppo di stelle. */
  label?: string;
  /** `id` opzionale per collegare label esterne. */
  id?: string;
}

/** Vincola un numero all'intervallo intero `[0, MAX_STARS]`. */
function clampScore(n: number): number {
  if (!Number.isFinite(n)) return 0;
  const int = Math.round(n);
  if (int < 0) return 0;
  if (int > MAX_STARS) return MAX_STARS;
  return int;
}

/** Voto minimo considerato sufficiente sulla scala `0..10`. */
export const PASSING_SCORE = 6;

/**
 * Mappa un voto `1..10` a un colore (HSL) che va dal rosso (voti bassi) al
 * verde (voti alti), usando `PASSING_SCORE` (6) come soglia di sufficienza.
 *
 *  - `1..6`  → dal rosso (`hue 0`) al giallo‑verde della sufficienza (`hue 90`);
 *  - `6..10` → dal giallo‑verde (`hue 90`) al verde pieno (`hue 135`).
 *
 * Restituisce `null` per voti non positivi (nessuna stella piena da colorare).
 */
export function scoreColor(score: number): string | null {
  if (!Number.isFinite(score) || score <= 0) return null;
  const s = clampScore(score);
  let hue: number;
  if (s <= PASSING_SCORE) {
    // Da rosso (1) a giallo‑verde (6).
    hue = ((s - 1) / (PASSING_SCORE - 1)) * 90;
  } else {
    // Da giallo‑verde (6) a verde pieno (10).
    hue = 90 + ((s - PASSING_SCORE) / (MAX_STARS - PASSING_SCORE)) * 45;
  }
  return `hsl(${Math.round(hue)}, 75%, 45%)`;
}

/**
 * Scala a 10 stelle intere. Renderizza sempre `MAX_STARS` stelle; il numero di
 * stelle piene riflette il voto (`0` = nessuna piena). Quando il valore è
 * `null` lo stato è marcato come "assente" e nessuna stella è piena.
 */
export function StarRating({
  value,
  onChange,
  readOnly = false,
  label = 'Voto',
  id,
}: StarRatingProps) {
  // Anteprima al passaggio del mouse/focus (non muta il valore reale).
  const [hover, setHover] = useState<number | null>(null);

  const isAbsent = value === null;
  const selected = isAbsent ? 0 : clampScore(value);
  // Numero di stelle da mostrare piene: hover in anteprima, altrimenti selezionato.
  const filledCount = hover ?? selected;
  // Colore delle stelle piene in base all'anteprima corrente (rosso→verde, 6 = sufficienza).
  const filledColor = scoreColor(filledCount);
  // Colore del box del voto: segue il voto reale selezionato (non l'anteprima hover).
  const statusColor = scoreColor(selected);

  const emit = (score: number) => {
    if (readOnly) return;
    onChange?.(clampScore(score));
  };

  const stars = Array.from({ length: MAX_STARS }, (_, i) => i + 1);

  return (
    <div
      className="star-rating"
      role="radiogroup"
      aria-label={label}
      aria-readonly={readOnly || undefined}
      data-absent={isAbsent || undefined}
      data-value={isAbsent ? '' : String(selected)}
      {...(id ? { id } : {})}
      onMouseLeave={() => setHover(null)}
    >
      {/* Controllo esplicito per il voto 0 (nessuna stella), distinto da null. */}
      <button
        type="button"
        className="star-rating__zero"
        aria-label="Voto 0 (nessuna stella)"
        aria-pressed={!isAbsent && selected === 0}
        disabled={readOnly}
        onClick={() => emit(0)}
        onMouseEnter={() => !readOnly && setHover(0)}
        onFocus={() => !readOnly && setHover(0)}
        onBlur={() => setHover(null)}
      >
        0
      </button>

      {/* Le stelle in un box riquadrato, così da allinearsi graficamente al
          pulsante "0" e al box del voto (tre riquadri sulla stessa riga). */}
      <span className="star-rating__stars">
        {stars.map((star) => {
          const filled = star <= filledCount;
          const isChecked = !isAbsent && star === selected;
          return (
            <button
              type="button"
              key={star}
              className={`star-rating__star${filled ? ' star-rating__star--filled' : ''}`}
              role="radio"
              aria-checked={isChecked}
              aria-label={`${star} ${star === 1 ? 'stella' : 'stelle'}`}
              disabled={readOnly}
              style={filled && filledColor ? { color: filledColor } : undefined}
              onClick={() => emit(star)}
              onMouseEnter={() => !readOnly && setHover(star)}
              onFocus={() => !readOnly && setHover(star)}
              onBlur={() => setHover(null)}
            >
              <span aria-hidden="true">{filled ? '★' : '☆'}</span>
            </button>
          );
        })}
      </span>

      {/* Box del voto a larghezza fissa: distingue assente / voto 0 / voto n.
          Colorazione "piena" (sfondo del colore della scala, testo bianco) come
          negli altri badge del software; il voto 0 e l'assenza restano neutri
          (`statusColor` è `null` per voti non positivi). */}
      <span
        className="star-rating__status"
        role="status"
        aria-label={isAbsent ? 'Non votato' : `Voto: ${selected}`}
        style={
          !isAbsent && statusColor
            ? { background: statusColor, borderColor: statusColor, color: '#fff' }
            : undefined
        }
      >
        {isAbsent ? '–' : selected}
      </span>
    </div>
  );
}

export default StarRating;
